import { useEffect } from "react";
import type { Configuration } from "../lib/config";
import { persistConfiguration } from "../lib/persistence";
import { decode } from "../lib/url-state";
import type { ConfigAction } from "./useConfiguration";
import type { Dispatch } from "react";

/**
 * Two-way sync between the configuration and the URL hash.
 *
 * - Outbound: every config change is written to the hash (and units to
 *   localStorage) via `replaceState`, so the URL is always shareable and
 *   history is never polluted.
 * - Inbound: external hash changes (address bar, back/forward) re-apply to
 *   state. `replaceState` does not emit `hashchange`, so our own writes never
 *   loop back here.
 */
export function useUrlSync(
  config: Configuration,
  dispatch: Dispatch<ConfigAction>,
): void {
  useEffect(() => {
    persistConfiguration(config);
  }, [config]);

  useEffect(() => {
    const onHashChange = () => {
      dispatch({ type: "replace", patch: decode(window.location.hash) });
    };
    window.addEventListener("hashchange", onHashChange);
    return () => window.removeEventListener("hashchange", onHashChange);
  }, [dispatch]);
}
